/**
 * All audio is synthesized with the Web Audio API — wind, spray hiss, and
 * muffled footsteps — so the game ships zero audio assets. The context is
 * created lazily on the first user gesture (browser autoplay policy).
 */
export class AudioManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private windGain: GainNode | null = null;
  private windFilter: BiquadFilterNode | null = null;
  private hissGain: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  muted: boolean;

  constructor() {
    this.muted = localStorage.getItem('snow-graffiti.muted') === '1';
  }

  /** Call from a user gesture (click / keydown). Safe to call repeatedly. */
  ensureStarted(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const ctx = new AudioContext();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = this.muted ? 0 : 1;
    this.master.connect(ctx.destination);

    // Shared 2s white-noise loop source material.
    const len = ctx.sampleRate * 2;
    this.noiseBuffer = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = this.noiseBuffer.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;

    // Wind: looped noise through a wandering low-pass.
    const windSrc = ctx.createBufferSource();
    windSrc.buffer = this.noiseBuffer;
    windSrc.loop = true;
    this.windFilter = ctx.createBiquadFilter();
    this.windFilter.type = 'lowpass';
    this.windFilter.frequency.value = 320;
    this.windFilter.Q.value = 0.8;
    this.windGain = ctx.createGain();
    this.windGain.gain.value = 0.02;
    windSrc.connect(this.windFilter).connect(this.windGain).connect(this.master);
    windSrc.start();

    // Spray hiss: the same noise through a band-pass, gated by spraying.
    const hissSrc = ctx.createBufferSource();
    hissSrc.buffer = this.noiseBuffer;
    hissSrc.loop = true;
    const hissFilter = ctx.createBiquadFilter();
    hissFilter.type = 'bandpass';
    hissFilter.frequency.value = 4200;
    hissFilter.Q.value = 0.6;
    this.hissGain = ctx.createGain();
    this.hissGain.gain.value = 0;
    hissSrc.connect(hissFilter).connect(this.hissGain).connect(this.master);
    hissSrc.start();
  }

  /** Wind follows snowfall intensity. */
  setWindLevel(intensity: number): void {
    if (!this.ctx || !this.windGain || !this.windFilter) return;
    const t = this.ctx.currentTime;
    this.windGain.gain.setTargetAtTime(0.012 + intensity * 0.09, t, 0.4);
    this.windFilter.frequency.setTargetAtTime(240 + intensity * 620, t, 0.4);
  }

  setSpraying(active: boolean): void {
    if (!this.ctx || !this.hissGain) return;
    this.hissGain.gain.setTargetAtTime(active ? 0.055 : 0, this.ctx.currentTime, 0.03);
  }

  /** Soft, muffled crunch. */
  footstep(): void {
    if (!this.ctx || !this.master || !this.noiseBuffer || this.muted) return;
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.playbackRate.value = 0.7 + Math.random() * 0.25;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 420 + Math.random() * 180;
    const gain = ctx.createGain();
    const t = ctx.currentTime;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.09, t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
    src.connect(filter).connect(gain).connect(this.master);
    src.start(t, Math.random() * 1.5, 0.2);
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    localStorage.setItem('snow-graffiti.muted', this.muted ? '1' : '0');
    if (this.ctx && this.master) {
      this.master.gain.setTargetAtTime(this.muted ? 0 : 1, this.ctx.currentTime, 0.05);
    }
    return this.muted;
  }
}
